// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import {
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
    HasOnSuccess,
    makeHandlerChainAwaitable,
} from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductModel, RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";
import loadProductListVariantChildren from "@west/App/Handlers/ProductList/LoadProductListVariantChildren";
import { getProductListVariantChildrenDataView } from "@west/App/Store/Selectors/ProductListSelectors";

type Parameter = {
    parentProductId: string;
};

type Props = {
    product: ProductModel;
    productInfosByProductId: SafeDictionary<ProductInfo>;
    variantChildren?: ProductModel[];
    pricingLoaded?: true;
    inventoryLoaded?: true;
};

type HandlerType = Handler<Parameter & HasOnSuccess, Props>;

export const RequestVariantChildrenFromApi: HandlerType = async props => {
    const product = getProductListDataView(props.getState())?.value?.find(
        product => product.properties.styleParentId === props.parameter.parentProductId,
    );

    if (!product || !product?.variantTraits || product.variantTraits.length === 0) {
        return false;
    }

    const isVariantChildrenExists = getProductListVariantChildrenDataView(
        props.getState(),
        props.parameter.parentProductId,
    );

    if (isVariantChildrenExists) {
        return false;
    }

    if (!isVariantChildrenExists) {
        props.variantChildren = await executeAwaitableHandlerChain(
            loadProductListVariantChildren,
            { productId: props.parameter.parentProductId },
            props,
        );

        props.dispatch({
            type: "xProductListVariantChildren/CompleteLoadProducts",
            parentProductId: props.parameter.parentProductId,
            variantChildrenProducts: props.variantChildren,
        } as any);
    }
};

export const SetUpProductInfos: HandlerType = props => {
    if (!props.variantChildren || props.variantChildren.length === 0) {
        return false;
    }

    props.productInfosByProductId = {};
    props.variantChildren?.forEach(product => {
        props.productInfosByProductId[product.id] = {
            ...createFromProduct(product),
        };
    });

    props.dispatch({
        type: "xProductListVariantChildren/CompleteLoadProductInfos",
        parentProductId: props.parameter.parentProductId,
        productInfosByProductId: props.productInfosByProductId,
    } as any);
};

export const LoadRealTimePrices: HandlerType = async props => {
    const { productInfosByProductId } = props;

    const productIds = Object.keys(productInfosByProductId);
    if (productIds.length === 0) {
        return;
    }

    const loadRealTimePricingParameter: Parameters<typeof loadRealTimePricing>[0] = {
        productPriceParameters: productIds.map(o => productInfosByProductId[o]!),
        onSuccess: realTimePricing => {
            props.dispatch({
                type: "xProductListVariantChildren/CompleteLoadRealTimePricing",
                parentProductId: props.parameter.parentProductId,
                realTimePricing,
            } as any);
            props.pricingLoaded = true;
        },
        onError: () => {
            props.dispatch({
                type: "xProductListVariantChildren/FailedLoadRealTimePricing",
                parentProductId: props.parameter.parentProductId,
            } as any);
            props.pricingLoaded = true;
        },
        onComplete(realTimePricingProps) {
            if (realTimePricingProps.apiResult) {
                this.onSuccess?.(realTimePricingProps.apiResult);
            } else if (realTimePricingProps.error) {
                this.onError?.(realTimePricingProps.error);
            }
        },
    };

    const settings = getSettingsCollection(props.getState());

    if (IS_SERVER_SIDE && getIsWebCrawler() && !settings.websiteSettings.includeRealTimeInventoryAndPricingForSeo) {
        const awaitableLoadRealTimePricing = makeHandlerChainAwaitable<
            Parameters<typeof loadRealTimePricing>[0],
            RealTimePricingModel
        >(loadRealTimePricing);
        const realTimePricing = await awaitableLoadRealTimePricing({
            productPriceParameters: loadRealTimePricingParameter.productPriceParameters,
        })(props.dispatch, props.getState);
        if (realTimePricing) {
            loadRealTimePricingParameter.onSuccess?.(realTimePricing);
        } else {
            loadRealTimePricingParameter.onError?.(realTimePricing);
        }
    } else {
        props.dispatch(loadRealTimePricing(loadRealTimePricingParameter));
    }

    const productSettings = settings.productSettings;
    if (productSettings.canSeePrices && productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const LoadRealTimeInventory: HandlerType = props => {
    if (
        !props.variantChildren?.length ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds: props.variantChildren.map(o => o.id),
            onSuccess: realTimeInventory => {
                props.dispatch({
                    type: "xProductListVariantChildren/CompleteLoadRealTimeInventory",
                    realTimeInventory,
                    parentProductId: props.parameter.parentProductId,
                } as any);
            },
            onError: () => {
                props.dispatch({
                    type: "xProductListVariantChildren/FailedLoadRealTimeInventory",
                    parentProductId: props.parameter.parentProductId,
                } as any);
            },
            onComplete(realTimeInventoryProps) {
                if (realTimeInventoryProps.apiResult) {
                    this.onSuccess?.(realTimeInventoryProps.apiResult);
                } else if (realTimeInventoryProps.error) {
                    this.onError?.(realTimeInventoryProps.error);
                }
            },
        }),
    );
};

export const ExecuteOnSuccessCallback: HandlerType = ({ parameter }) => {
    parameter.onSuccess?.();
};

export const chain = [
    RequestVariantChildrenFromApi,
    SetUpProductInfos,
    LoadRealTimePrices,
    LoadRealTimeInventory,
    ExecuteOnSuccessCallback,
];

const displayProductsVariantChildren = createHandlerChainRunner(chain, "DisplayProductsVariantChildren");
export default displayProductsVariantChildren;
